import type { ApolloClient } from '@apollo/client';
import { Upload } from 'tus-js-client';
import { GET_MEDIA_VIDEO, START_VIDEO_UPLOAD } from '../../graphql';

/**
 * Video: device → Cloudflare Stream, never through our API server.
 *
 * The server hands back a one-time upload URL for exactly this file (capped at
 * the minutes the plan has left, unusable after an hour) — no credential ever
 * reaches the page. The file goes up in resumable pieces (tus), so a dropped
 * phone connection picks up where it stopped instead of starting over.
 * Stream then encodes it; waitForVideo polls until it's ready.
 */

export interface VideoInfo {
  status: 'uploading' | 'processing' | 'ready' | 'error' | 'rejected';
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  posterUrl: string | null;
  posterAssetId?: string | null;
  posterTimeSeconds?: number | null;
  frameUrl?: string | null;
  playerUrl: string | null;
  mp4Url: string | null;
  mp4Status: string | null;
  error: string | null;
}

export const isVideoFile = (file: File) =>
  file.type.startsWith('video/') || /\.(mp4|m4v|mov|webm|mkv|avi)$/i.test(file.name);

const CHUNK = 50 * 1024 * 1024;

/** Upload one video; resolves with its library asset id once the bytes are in. */
export async function uploadVideo(
  client: ApolloClient<unknown>,
  accountId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ assetId: string; maxDurationSeconds: number }> {
  const { data } = await client.mutate({
    mutation: START_VIDEO_UPLOAD,
    variables: { accountId, sizeBytes: file.size, name: file.name },
  });
  const start = data?.startVideoUpload;
  if (!start?.uploadUrl) throw new Error('The upload couldn’t be started');

  await new Promise<void>((resolve, reject) => {
    const upload = new Upload(file, {
      uploadUrl: start.uploadUrl,
      uploadSize: file.size,
      chunkSize: CHUNK,
      retryDelays: [0, 2000, 5000, 10000, 20000],
      storeFingerprintForResuming: false,
      onProgress: (sent, total) => onProgress?.(total ? Math.floor((sent / total) * 100) : 0),
      onError: (err) => reject(new Error(friendlyTusError(err))),
      onSuccess: () => resolve(),
    });
    upload.start();
  });
  return { assetId: start.assetId, maxDurationSeconds: start.maxDurationSeconds };
}

function friendlyTusError(err: Error): string {
  const msg = String(err?.message || '');
  if (/413|too large|exceeds/i.test(msg)) return 'The video is larger than allowed';
  if (/40[03]|duration/i.test(msg)) return 'The video is longer than your plan has minutes left';
  return 'The upload stopped. Check your connection and try again.';
}

/**
 * Wait until Stream has finished with a video. With `needMp4` (a social post)
 * also wait for the MP4 that social networks are given.
 */
export async function waitForVideo(
  client: ApolloClient<unknown>,
  accountId: string,
  assetId: string,
  {
    needMp4 = false,
    timeoutMs = 15 * 60 * 1000,
    onStatus,
  }: {
    needMp4?: boolean;
    timeoutMs?: number;
    onStatus?: (v: VideoInfo) => void;
  } = {}
): Promise<VideoInfo> {
  const until = Date.now() + timeoutMs;
  for (;;) {
    const { data } = await client.query({
      query: GET_MEDIA_VIDEO,
      variables: { accountId, assetId },
      fetchPolicy: 'network-only',
    });
    const v: VideoInfo | null = data?.mediaVideo?.video ?? null;
    if (v) onStatus?.(v);
    if (v?.status === 'error' || v?.status === 'rejected') {
      throw new Error(v.error || 'The video couldn’t be processed');
    }
    if (v?.status === 'ready' && (!needMp4 || v.mp4Url)) return v;
    if (Date.now() > until)
      throw new Error('The video is still processing. Check back in a few minutes.');
    await new Promise((r) => setTimeout(r, 4000));
  }
}

export const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds && seconds !== 0) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
